import { useEffect, useRef } from "react";

// Climate profile lookup. Maps US state codes to a renderer profile that
// adjusts sun intensity, hemisphere tint, sky background, and fog so designs
// in (e.g.) Phoenix feel sun-baked while Seattle reads cool + overcast.
//   sunMult:        multiplier applied to the time-of-day sun intensity.
//   fogColor:       atmospheric tint (hex).
//   fogNear/far:    visibility falloff in metres.
//   bgColor:        scene clear colour behind the sky shader.
//   hemiSky/Ground: hemisphere light upper / lower colours.
//   hemiIntensity:  hemisphere light strength.
// V2.0.22 — Daytime sky-blue palette. Each climate keeps its character
// (warmer haze in desert, cool-blue in PNW, vibrant cerulean in alpine)
// but ALL bgColors now read as "sky" instead of the old midnight navy.
export const CLIMATE_PROFILES = {
  desert: {
    sunMult: 1.30, fogColor: 0xe6cfa6, fogNear: 60, fogFar: 160,
    bgColor: 0xcfd6c8, hemiSky: 0xf4d9a8, hemiGround: 0x3a2a18, hemiIntensity: 0.7,
  },
  humid: {
    sunMult: 0.95, fogColor: 0xc4d8e2, fogNear: 45, fogFar: 130,
    bgColor: 0xa9c8db, hemiSky: 0xb6d4ec, hemiGround: 0x1f3340, hemiIntensity: 0.65,
  },
  pnw: {
    sunMult: 0.70, fogColor: 0xb9c3cc, fogNear: 35, fogFar: 110,
    bgColor: 0x9aa9b6, hemiSky: 0x9eaab6, hemiGround: 0x202830, hemiIntensity: 0.85,
  },
  alpine: {
    sunMult: 1.10, fogColor: 0xd8e6f0, fogNear: 60, fogFar: 160,
    bgColor: 0x8ab8de, hemiSky: 0xd0e4f4, hemiGround: 0x223344, hemiIntensity: 0.6,
  },
  tropical: {
    sunMult: 1.05, fogColor: 0xc5d9ce, fogNear: 50, fogFar: 140,
    bgColor: 0xa6cad6, hemiSky: 0xc6e0d2, hemiGround: 0x1f3030, hemiIntensity: 0.7,
  },
  temperate: {
    sunMult: 1.00, fogColor: 0xb8d8ec, fogNear: 55, fogFar: 140,
    bgColor: 0x9ccdf2, hemiSky: 0xbcd4ff, hemiGround: 0x223344, hemiIntensity: 0.55,
  },
};

const STATE_TO_CLIMATE = {
  AZ: "desert", NV: "desert", NM: "desert", UT: "desert",
  TX: "humid", LA: "humid", MS: "humid", AL: "humid", GA: "humid",
  SC: "humid", NC: "humid", AR: "humid", OK: "humid",
  FL: "tropical", HI: "tropical",
  WA: "pnw", OR: "pnw", AK: "pnw",
  CO: "alpine", MT: "alpine", WY: "alpine", ID: "alpine",
  ND: "alpine", SD: "alpine", MN: "alpine", VT: "alpine",
  NH: "alpine", ME: "alpine",
  CA: "temperate", NY: "temperate", NJ: "temperate", PA: "temperate",
  MA: "temperate", CT: "temperate", RI: "temperate", DE: "temperate",
  MD: "temperate", VA: "temperate", WV: "temperate", OH: "temperate",
  IN: "temperate", IL: "temperate", MI: "temperate", WI: "temperate",
  IA: "temperate", MO: "temperate", KS: "temperate", NE: "temperate",
  KY: "temperate", TN: "temperate", DC: "temperate",
};

export function pickClimateProfile(stateCode, zipCode) {
  let key = STATE_TO_CLIMATE[String(stateCode || "").toUpperCase()];
  if (!key && zipCode) {
    const z = String(zipCode).match(/^\d/);
    if (z) {
      const d = z[0];
      if (d === "8" || d === "9") key = "desert";
      else if (d === "3") key = "humid";
      else if (d === "0" || d === "1") key = "temperate";
    }
  }
  return CLIMATE_PROFILES[key] || CLIMATE_PROFILES.temperate;
}

/**
 * Climate-aware rendering hook.
 * Adjusts fog/bg/hemi/sun-intensity multiplier whenever the build-site
 * metadata (or timeOfDay) changes. Owns the `climateSunMultRef` that is
 * read by the time-of-day effect.
 *
 * @param {object} blueprint               Current blueprint (reads build_site_metadata).
 * @param {number} timeOfDay               Hour 0..24.
 * @param {object} refs                    { sceneRef, sunRef, hemiRef }
 * @returns {object} { climateSunMultRef }
 */
export default function useClimate(blueprint, timeOfDay, { sceneRef, sunRef, hemiRef }) {
  const climateSunMultRef = useRef(1.0);

  useEffect(() => {
    const scene = sceneRef.current;
    const sun = sunRef.current;
    const hemi = hemiRef.current;
    if (!scene) return;

    const meta = (blueprint && blueprint.build_site_metadata) || {};
    const state = String(meta.state || "").toUpperCase().trim();
    const profile = pickClimateProfile(state, meta.zip_code);
    climateSunMultRef.current = profile.sunMult;

    if (scene.fog) {
      scene.fog.color.setHex(profile.fogColor);
      scene.fog.near = profile.fogNear;
      scene.fog.far = profile.fogFar;
    }
    if (scene.background && scene.background.isColor) {
      scene.background.setHex(profile.bgColor);
    }
    if (hemi) {
      hemi.color.setHex(profile.hemiSky);
      hemi.groundColor.setHex(profile.hemiGround);
      hemi.intensity = profile.hemiIntensity;
    }
    if (sun) {
      const t = Math.max(0, Math.min(24, Number(timeOfDay) || 12));
      const dayProgress = (t - 5) / 14;
      const isDaytime = dayProgress >= 0 && dayProgress <= 1;
      const angle = Math.PI * Math.max(0, Math.min(1, dayProgress));
      const noonness = isDaytime ? Math.sin(angle) : 0;
      sun.intensity = (isDaytime ? 0.6 + 1.0 * noonness : 0.05) * profile.sunMult;
    }
  }, [blueprint?.build_site_metadata, timeOfDay, sceneRef, sunRef, hemiRef]);

  return { climateSunMultRef };
}
